import { A as AdapterBaseHtmlMedia } from './adapter-base-htmlmedia.js';
import { C as CoreEvent } from './event.js';
import { g as getMatchLanguages, a as getClientLanguages, o as orderByLanguage, i as implementsStatic } from './time-range.js';
import { g as getLogger } from './logger.js';
import { P as PlaybackSessionErrorType, a as PlaybackSessionError } from './playback-session-error.js';
import { DestroyableAbortController, DestroyableSet, whenDestroyed, destroyableEventListener, destroyableTimeout, DestroyableBase } from '@nrk/destroyable';
import { c as canPlayType } from './can-play-type2.js';
import { d as dimensionsToOrientation } from './fetch.js';
import { l as loadHlsMediaPlaylist } from './hls-media-playlist.js';

// Since hls.js is optional, we can only use the types, and the types must not
const logger$1 = getLogger('HlsJsBuiltinSubtitles');
/**
 * Only certain combinations of default, autoselect and forced are allowed by
 * spec. The implementation doesn't assume the spec is followed. Forced trumps
 * all. Default implies autoselect.
 *
 * hls.js supports multiple text track groups. player-core doesn't (no
 * mechanism for swapping the list of available subtitles).
 */

class HlsJsBuiltinSubtitles {
  constructor(hlsClass, hls, eventEmitter) {
    this.hlsClass = void 0;
    this.hls = void 0;
    this.eventEmitter = void 0;
    this.activeTrack = void 0;
    this.hlsClass = hlsClass;
    this.hls = hls;
    this.eventEmitter = eventEmitter;
  }

  supportsAddingTracks() {
    return false;
  }

  addSubtitlesTrack() {
    throw new Error('Not supported.');
  }

  setNoSubtitles(options) {
    const forcedTrack = this.getForcedTrack();
    this.switchSubtitles(forcedTrack == null ? void 0 : forcedTrack.id, options);
  }

  setDefaultSubtitles() {
    const options = {
      byUser: false
    };
    const defaultTrack = this.getDefaultTrack();

    if (defaultTrack) {
      this.switchSubtitles(defaultTrack.id, options);
      return;
    }

    this.setNoSubtitles(options);
  }

  setAutoSubtitles() {
    const options = {
      byUser: false
    };
    const defaultTrack = this.getDefaultTrack();

    if (defaultTrack) {
      this.switchSubtitles(defaultTrack.id, options);
      return;
    }

    const autoselectTrack = this.getAutoselectTrack();

    if (autoselectTrack) {
      this.switchSubtitles(autoselectTrack.id, options);
      return;
    }

    this.setNoSubtitles(options);
  }

  setActiveSubtitles(id, options) {
    const ident = Number.parseInt(typeof id === 'object' ? id.id : id, 10);

    if (this.getAllTracks().find(track => track.id === ident && !track.forced)) {
      this.switchSubtitles(ident, options);
    }
  }

  getActiveSubtitles() {
    const id = this.hls.subtitleTrack;

    if (id === -1) {
      return undefined;
    }

    const track = this.getAllTracks().find(t => t.id === id && !t.forced);
    return track ? trackToSubtitle(track) : undefined;
  }

  getAvailableSubtitles() {
    return this.getAllTracks().filter(sub => !sub.forced).map(trackToSubtitle);
  }

  enableNativeSubtitlesDisplay() {
    if (!this.hls.subtitleDisplay) {
      this.hls.subtitleDisplay = true;
      this.updateCurrentTrackMode(); // hls.js doesn't do this by itself.

      this.emitSubtitlesDisplayChange();
    }
  }

  disableNativeSubtitlesDisplay() {
    if (this.hls.subtitleDisplay) {
      this.hls.subtitleDisplay = false;
      this.updateCurrentTrackMode(); // hls.js doesn't do this by itself.

      this.emitSubtitlesDisplayChange();
    }
  }

  isNativeSubtitlesDisplayEnabled() {
    return this.hls.subtitleDisplay;
  }

  destroy() {
    this.switchSubtitles(undefined, {
      byUser: false
    });
    disableHlsJsTextTrackController(this.hls);
  }

  getDefaultTrack() {
    return this.getBestLanguageMatch(this.getAllTracks().filter(track => track.default && !track.forced));
  }

  getAutoselectTrack() {
    return this.getBestLanguageMatch(this.getAllTracks().filter(track => (track.autoselect || track.default) && !track.forced));
  }

  getForcedTrack() {
    return this.getBestLanguageMatch(this.getAllTracks().filter(track => track.forced));
  }

  getBestLanguageMatch(tracks) {
    if (tracks.length === 0) {
      return undefined;
    }

    const preferredLanguages = getMatchLanguages(getClientLanguages());
    return orderByLanguage(tracks, track => {
      var _track$lang;

      return (_track$lang = track.lang) != null ? _track$lang : 'unknown';
    }, preferredLanguages)[0];
  }

  getAllTracks() {
    return this.hls.subtitleTracks;
  }

  switchSubtitles(targetTrackId, options) {
    let newTrackData; // Setting the subtitleTrack will trigger a SUBTITLE_TRACK_SWITCH event
    // synchronously if the track actually changes.

    const captureSwitchEvent = (event, data) => {
      newTrackData = data;
    };

    this.hls.on(this.hlsClass.Events.SUBTITLE_TRACK_SWITCH, captureSwitchEvent);
    this.hls.subtitleTrack = targetTrackId != null ? targetTrackId : -1;
    this.hls.off(this.hlsClass.Events.SUBTITLE_TRACK_SWITCH, captureSwitchEvent);

    if (!newTrackData) {
      logger$1.log(`Received no SUBTITLE_TRACK_SWITCH event for attempted switch to ${targetTrackId != null ? targetTrackId : -1}. Current track is: ${this.hls.subtitleTrack}`);
      return;
    } // When hls.js switches track we need to:
    // - Stop listening for cues on any current track.
    // - Clear any current cues.
    // - Start listening for cues on any new track.
    // - Emit subtitles change event.
    //
    // hls.js doesn't provide us with any cue events or a reference to the
    // TextTrack, so we need to find it ourselves to attach a cue listener.


    if (this.activeTrack !== undefined) {
      this.activeTrack.oncuechange = null; // Clear any current cues. The activeCues list doesn't exist (null,
      // Chrome) on disabled tracks, so not going to check if it's actually
      // needed.

      this.emitCueEvent();
      this.activeTrack = undefined;
    }

    if (newTrackData.id !== -1) {
      this.activeTrack = this.findActiveHlsJsTextTrack(newTrackData);

      if (this.activeTrack !== undefined) {
        this.activeTrack.oncuechange = this.emitCueEvent.bind(this, this.activeTrack);
      } else {
        logger$1.error('Unable to find the text track matching:', newTrackData);
      }
    }

    this.emitSubtitlesChange((options == null ? void 0 : options.byUser) !== false);
  }
  /**
   * hls.js doesn't change between hidden and showing track modes when toggling
   * subtitleDisplay, so get around the problem by deselecting and selecting
   * the current track, if any.
   */


  updateCurrentTrackMode() {
    const currentTrack = this.hls.subtitleTrack;

    if (currentTrack !== -1) {
      this.hls.subtitleTrack = -1;
      this.hls.subtitleTrack = currentTrack;
    }
  }
  /**
   * Find the active text track object that matches the track data from hls.js.
   */


  findActiveHlsJsTextTrack(data) {
    var _this$hls$media$textT, _this$hls$media;

    return Array.from((_this$hls$media$textT = (_this$hls$media = this.hls.media) == null ? void 0 : _this$hls$media.textTracks) != null ? _this$hls$media$textT : []).find(track => {
      return track.kind === 'subtitles' && track.mode !== 'disabled' && track.label === data.name && (!data.groupId || track.groupId === data.groupId);
    });
  }

  emitSubtitlesChange(byUser) {
    this.eventEmitter.emit(CoreEvent.SubtitlesChange, {
      byUser,
      active: this.getActiveSubtitles() // Forced not returned.

    });
  }

  emitSubtitlesDisplayChange() {
    this.eventEmitter.emit(CoreEvent.SubtitlesDisplayChange, {
      nativeEnabled: this.hls.subtitleDisplay
    });
  }

  emitCueEvent(track) {
    var _track$activeCues;

    this.eventEmitter.emit(CoreEvent.SubtitlesCueUpdate, {
      activeCues: Array.from((_track$activeCues = track == null ? void 0 : track.activeCues) != null ? _track$activeCues : [])
    });
  }

}

function trackToSubtitle(track) {
  var _track$lang2;

  return {
    id: `${track.id}`,
    label: track.name,
    language: (_track$lang2 = track.lang) != null ? _track$lang2 : 'unknown'
  };
}
/**
 * Disconnect the hls.js text track controller from the media, because it
 * interferes with custom subtitle handling. The disconnect is temporary.
 * Attaching new media to the same hls.js instance will automatically re-enable
 * the controller.
 */


function disableHlsJsTextTrackController(hls) {
  // @ts-expect-error - Accessing hls.js internal properties.
  const controller = hls.subtitleTrackController;
  logger$1.log('Disabling hls.js text track controller');
  controller.onMediaDetaching();
}

/**
 * Holds error information extracted from hls.js ErrorData objects, and can be
 * safely serialized with JSON.stringify (unlike ErrorData).
 */

class ReducedHlsJsError extends Error {
  constructor(errorData) {
    var _errorData$response, _errorData$context;

    super(errorData.details);
    this.url = void 0;
    this.type = void 0;
    this.reason = void 0;
    this.fatal = void 0;
    this.response = void 0;
    this.context = void 0;
    Object.setPrototypeOf(this, ReducedHlsJsError.prototype);
    this.name = 'ReducedHlsJsError';
    this.url = errorData.url;
    this.type = errorData.type;
    this.reason = errorData.reason;
    this.fatal = errorData.fatal; // @ts-expect-error - The types and values of errorData.response don't match.

    const {
      text,
      code
    } = (_errorData$response = errorData.response) != null ? _errorData$response : {};
    const {
      level,
      url,
      type
    } = (_errorData$context = errorData.context) != null ? _errorData$context : {};
    this.response = {
      text,
      code
    };
    this.context = {
      level: level !== null ? level : undefined,
      url,
      type
    };
  }

}
/**
 * Resolve the PlaybackSessionErrorType of an hls.js ErrorData object.
 */

function resolveHlsJsErrorType(data, Details, playbackStarted) {
  let errorType = PlaybackSessionErrorType.UNKNOWN_ERROR; // Fatal errors only, see
  // https://github.com/video-dev/hls.js/blob/master/docs/API.md#Errors

  switch (data.details) {
    case Details.FRAG_LOAD_ERROR:
    case Details.FRAG_LOAD_TIMEOUT:
    case Details.KEY_LOAD_TIMEOUT:
    case Details.LEVEL_LOAD_ERROR:
    case Details.LEVEL_LOAD_TIMEOUT:
    case Details.MANIFEST_LOAD_ERROR:
    case Details.MANIFEST_LOAD_TIMEOUT:
      errorType = playbackStarted ? PlaybackSessionErrorType.PLAYBACK_STOPPED_NETWORK_ERROR : PlaybackSessionErrorType.NO_PLAYBACK_NETWORK_ERROR;
      break;

    case Details.FRAG_DECRYPT_ERROR:
    case Details.FRAG_PARSING_ERROR:
    case Details.MANIFEST_PARSING_ERROR:
      errorType = playbackStarted ? PlaybackSessionErrorType.PLAYBACK_STOPPED_DECODE_ERROR : PlaybackSessionErrorType.NO_PLAYBACK_DECODE_ERROR;
      break;

    case Details.MANIFEST_INCOMPATIBLE_CODECS_ERROR:
      errorType = PlaybackSessionErrorType.UNSUPPORTED_FORMAT_ERROR;
      break;

    case Details.BUFFER_APPEND_ERROR:
    case Details.BUFFER_STALLED_ERROR:
  }

  return errorType;
}

function round(n, decimals = 0) {
  const factor = 10 ** decimals;
  return Math.round(n * factor) / factor;
}

var _dec, _class;
const adapterInfo = {
  id: 'HlsJs',
  name: 'Hls.js adapter',
  tech: 'hls.js',
  version: 'hls.js not yet loaded'
};
/**
 * The duration and media range values are rounded off slightly to combat
 * "duplicate" duration change events where the second event event has more
 * decimals than the first. The number of decimals to round off to:
 */

const MEDIA_RANGE_PRECISION = 1;
/**
 * How far ahead of nextLoadPosition (hls.js internal) or currentTime in the
 * source buffers is considered future buffer that should be cleared if the
 * flushAheadBufferOnSeek option is set. In seconds.
 */

const AHEAD_BUFFER_DISTANCE = 60;
const logger = getLogger('HlsJsAdapter');
let HlsJsAdapter = (_dec = implementsStatic(), _dec(_class = class HlsJsAdapter extends AdapterBaseHtmlMedia {
  constructor(context, loadHlsJs, options) {
    super(context);
    this.hlsJsLoader = void 0;
    this.qualityTracker = void 0;
    this.mediaInfo = void 0;
    this.options = void 0;
    this.continueLoadingMediaOnce = void 0;
    this.incomingAutoSeek = void 0;
    this.startTime = void 0;
    this.startTimeLive = void 0;
    this.hlsJsLoader = loadHlsJs;
    this.mediaInfo = { ...context.media
    };
    this.mediaInfo.isAudioOnly = false;
    this.options = options;
    this.qualityTracker = context.qualityTracker;
    this.applyInitialPlaybackState(context.initialPlaybackState);
  }

  static canPlayType(mediaType) {
    return canPlayType(mediaType);
  }

  static getAdapterInfo() {
    return adapterInfo;
  }

  getAdapterInfo() {
    return { ...adapterInfo,
      config: { ...this.options
      }
    };
  }

  getMediaInfo() {
    return this.mediaInfo;
  }

  async internalPreload(preloadDestroyables, options) {
    var _this$options$limitQu, _this$options, _this$options2, _this$options3, _this$options4;

    const config = {
      autoStartLoad: false,
      // Load master playlist only.
      capLevelToPlayerSize: (_this$options$limitQu = (_this$options = this.options) == null ? void 0 : _this$options.limitQualityToPlayerSize) != null ? _this$options$limitQu : true,
      debug: (_this$options2 = this.options) == null ? void 0 : _this$options2.debugHlsJs,
      enableWorker: !((_this$options3 = this.options) != null && _this$options3.disableWebWorker)
    };

    if (((_this$options4 = this.options) == null ? void 0 : _this$options4.backBufferLength) !== undefined) {
      config.backBufferLength = this.options.backBufferLength;
    }

    const hlsClass = await this.loadHlsJs();
    const hls = new hlsClass(config);
    this.addEventListener('destroy', () => hls.destroy());
    this.registerHlsJsEventListeners(hlsClass, hls);
    await this.startLoadingMedia(hlsClass, hls); // startLoadingMedia() has its own error listener. Take over now that
    // startLoadingMedia() is done.

    this.registerHlsJsErrorListener(hlsClass, hls); // Prepare continueLoadingMedia so that it can be called without arguments.

    this.continueLoadingMediaOnce = destroyables => this.continueLoadingMedia(hlsClass, hls, destroyables);

    if ((options == null ? void 0 : options.loadData) !== false || this.startTimeLive !== undefined) {
      var _this$continueLoading;

      await ((_this$continueLoading = this.continueLoadingMediaOnce) == null ? void 0 : _this$continueLoading.call(this, preloadDestroyables));
    } else {
      // Resolve isLive without hls.js.
      const abortController = new DestroyableAbortController();
      preloadDestroyables.addDestroyable(abortController);
      const hlsMediaPlaylist = await loadHlsMediaPlaylist(hls.levels[hls.firstLevel].uri, {
        signal: abortController.signal
      });
      const playlistEnd = hlsMediaPlaylist.playlistDuration;
      this.handleMediaRangeUpdate({
        start: 0,
        end: playlistEnd,
        complete: hlsMediaPlaylist.fixedEnd,
        timestamp: Date.now() / 1000
      }, playlistEnd - 3 * hlsMediaPlaylist.averageSegmentDuration // Wait with program date/time until hls.js loads it.
      );
    }
  }

  async internalPlay(requiredState) {
    var _this$continueLoading2;

    await ((_this$continueLoading2 = this.continueLoadingMediaOnce) == null ? void 0 : _this$continueLoading2.call(this, this));
    await super.internalPlay(requiredState);
  }
  /**
   * hls.js auto-seeks over buffer holes or nudges stalled buffers by
   * auto-seeking a little. See registerHlsJsErrorListener().
   */


  isAutoSeek() {
    if (this.incomingAutoSeek !== undefined) {
      if (Date.now() - this.incomingAutoSeek < 50) {
        logger.log('Seeking event associated with buffer management. Not changing state.');
        return true;
      }

      this.incomingAutoSeek = undefined;
    }

    return false;
  }
  /**
   * Apply the initial playback state to the adapter. Start times are just
   * stored for later.
   */


  applyInitialPlaybackState(state) {
    const {
      playbackRate,
      mute,
      volume,
      startTime,
      startTimeLive
    } = state != null ? state : {};

    if (playbackRate !== undefined) {
      this.setPlaybackRate(playbackRate);
    }

    if (mute === true) {
      this.mute();
    } else if (mute === false) {
      this.unmute();
    }

    if (volume !== undefined) {
      this.setVolume(volume);
    }

    this.startTime = startTime;
    this.startTimeLive = startTimeLive;
  }
  /**
   * Load hls.js using the method provided in the adapter configuration.
   * There's no validation of what's actually loaded. The adapter will just
   * fail if it's not hls.js.
   */


  async loadHlsJs() {
    const classOrModule = await this.hlsJsLoader();
    const unknownClass = typeof classOrModule === 'function' ? classOrModule : classOrModule.default;
    const hlsClass = unknownClass; // Note the version in the adapter info.

    adapterInfo.version = hlsClass.version;
    return hlsClass;
  }

  async startLoadingMedia(hlsClass, hls) {
    var _masterPlaylist$subti;

    const loadingHooks = new DestroyableSet(this);
    let masterPlaylist;
    loadingHooks.addDestroyables([on(hls, hlsClass.Events.ERROR, (event, data) => {
      const error = new PlaybackSessionError(resolveHlsJsErrorType(data, hlsClass.ErrorDetails, this.playbackStarted), new ReducedHlsJsError(data), data.details, data.fatal);

      if (data.fatal) {
        loadingHooks.destroy();
        hls.destroy();
      }

      this.handleError(error);
    }), on(hls, hlsClass.Events.MEDIA_ATTACHED, () => {
      logger.log('Media attached, loading source.');
      hls.loadSource(this.mediaInfo.url);
    }), on(hls, hlsClass.Events.MANIFEST_PARSED, (event, data) => {
      logger.log('Manifest parsed.');
      masterPlaylist = data;
      loadingHooks.destroy();
    })]);
    hls.attachMedia(this.mediaElement);
    await whenDestroyed(loadingHooks);

    if (masterPlaylist === undefined) {
      return;
    }

    this.completeMediaInfo(masterPlaylist);

    if (this.mediaInfo.qualities !== undefined) {
      this.qualityTracker.setAvailableQualities(this.mediaInfo.qualities);
    } // Don't replace any existing subtitle controls. Existing controls
    // means that the media provided subtitles are not going to be used.


    if ((_masterPlaylist$subti = masterPlaylist.subtitleTracks) != null && _masterPlaylist$subti.length && this.subtitleControls === undefined) {
      logger.log('Creating controls for media provided subtitles:', masterPlaylist.subtitleTracks.map(t => t.name).join(', '));
      this.subtitleControls = new HlsJsBuiltinSubtitles(hlsClass, hls, this.eventEmitter);
    } else {
      disableHlsJsTextTrackController(hls);
    } // hls.js enables any default subtitles track just after emitting the
    // SUBTITLE_TRACKS_UPDATED event, so "our choice of" default must be
    // applied afterwards.


    if (this.subtitleControls !== undefined) ;
  }
  /**
   * Load media content, potentially with start time. If start time is applied
   * depends on the type of stream, so metadata preloading needs to happen
   * before start time can be applied.
   */


  async continueLoadingMedia(hlsClass, hls, destroyables) {
    var _this$startTime, _this$startTime2;

    this.continueLoadingMediaOnce = undefined;
    logger.log(`Starting to load data with start time: ${(_this$startTime = this.startTime) != null ? _this$startTime : -1} (start time live: ${this.startTimeLive})`);
    hls.startLoad((_this$startTime2 = this.startTime) != null ? _this$startTime2 : -1); // Wait for loadedmetadata (triggered by hls.js after loading the first
    // level) and then potentially seek to startTimeLive.

    const wait = destroyableEventListener(this.mediaElement, 'loadedmetadata', () => {
      logger.log('Got loadedmetadata.');

      if (this.startTimeLive !== undefined) {
        logger.log('Seeking to startTimeLive.');
        this.seekToLiveTime(this.startTimeLive);
      }

      wait.destroy();
    });
    destroyables.addDestroyable(wait);
    await whenDestroyed(wait);
  }
  /**
   * Update the media info, quality tracker and subtitle controls with data
   * from the HLS master playlist.
   */


  completeMediaInfo(manifest) {
    const isAudioOnly = !!manifest.audio && !manifest.video;
    this.mediaInfo.isAudioOnly = isAudioOnly;
    this.mediaInfo.qualities = manifest.levels.map(({
      bitrate,
      width,
      height,
      attrs
    }, index) => ({
      level: index,
      bitrate,
      averageBitrate: attrs['AVERAGE-BANDWIDTH'] ? Number.parseInt(attrs['AVERAGE-BANDWIDTH'], 10) : undefined,
      frameRate: attrs['FRAME-RATE'] !== undefined ? parseFloat(attrs['FRAME-RATE']) : undefined,
      // hls.js defaults to 0x0 dimensions for audio only streams.
      // player-core defaults to undefined.
      width: isAudioOnly ? undefined : width,
      height: isAudioOnly ? undefined : height
    }));
    this.mediaInfo.firstQuality = { ...this.mediaInfo.qualities[manifest.firstLevel]
    };
    const dimensions = this.mediaInfo.qualities.find(quality => quality.width !== undefined && quality.height !== undefined);

    if (dimensions !== undefined) {
      this.mediaInfo.videoOrientation = dimensionsToOrientation(dimensions.width, dimensions.height);
    }
  }

  registerHlsJsEventListeners(hlsClass, hls) {
    var _this$options5, _this$options6;

    hls.on(hlsClass.Events.LEVEL_SWITCHED, (event, data) => {
      this.qualityTracker.setCurrentQualityByLevel(data.level);
    });
    hls.on(hlsClass.Events.LEVEL_UPDATED, (event, data) => {
      var _details$averagetarge;

      const timestamp = Date.now() / 1000;
      const details = data.details;
      const playlistDuration = details.totalduration;

      if (playlistDuration <= 0) {
        return;
      }

      const start = round(details.fragments[0].start, MEDIA_RANGE_PRECISION);
      const mediaRange = {
        start,
        end: start + round(playlistDuration, MEDIA_RANGE_PRECISION),
        complete: !details.live,
        timestamp
      };
      const averageTargetDuration = (_details$averagetarge = details.averagetargetduration) != null ? _details$averagetarge : details.targetduration;
      const livePosition = mediaRange.end - 3 * averageTargetDuration;
      const programDateTimeMs = details.fragments[0].programDateTime;
      const programDateTime = programDateTimeMs ? programDateTimeMs / 1000 : // Make a guess, but only once.
      this.programDateTimeAtPosZero === undefined ? this.guessHlsProgramDateTime(playlistDuration, averageTargetDuration) : undefined;
      this.handleMediaRangeUpdate(mediaRange, livePosition, programDateTime);
    }); // Not technically an hls.js event listener, but never mind. Like hls.js
    // supports clearing the back buffer with the backBufferLength setting,
    // this takes care of any ahead buffer when seeking backwards.

    if (((_this$options5 = this.options) == null ? void 0 : _this$options5.flushAheadBufferOnSeek) === true) {
      this.addDestroyable(destroyableEventListener(this.mediaElement, 'seeked', () => {
        const currentTime = this.mediaElement.currentTime;
        const buffered = this.mediaElement.buffered;
        let startOffset;
        let endOffset; // Find the buffered ranges at a minimum distance from current time and
        // not part of forward buffer, and flush them.

        for (let i = 1; i < buffered.length; ++i) {
          if (buffered.start(i) > currentTime + AHEAD_BUFFER_DISTANCE && buffered.start(i) - buffered.end(i - 1) > hls.config.maxBufferHole) {
            startOffset = buffered.start(i);
            endOffset = buffered.end(buffered.length - 1);
            break;
          }
        }

        if (startOffset !== undefined && endOffset !== undefined) {
          logger.log(`Flushing ahead buffer after seek: ${startOffset} to ${endOffset}`);
          hls.trigger(hlsClass.Events.BUFFER_FLUSHING, {
            startOffset,
            endOffset,
            type: null
          });
        }
      }));
    } // hls.js switches levels by changing which segments to load next, which
    // means that all the already loaded segments are played out before the
    // level switch becomes visible to the user.
    //
    // Implemented here: When going fullscreen or otherwise if hls.js signals a
    // large quality jump, then change quality level immediately instead of
    // waiting for already loaded segments to be played out.
    //
    // A significant quality change is defined as the horizontal resolution of
    // the video doubling or more.


    if ((_this$options6 = this.options) != null && _this$options6.immediateQualityJumps) {
      const MIN_WIDTH_RATIO = 2;
      let switching = false;
      hls.on(hlsClass.Events.LEVEL_SWITCHING, (event, data) => {
        // No current quality at startup. Ignore situation.
        if (hls.currentLevel === -1) {
          return;
        } // Setting hls.currentLevel to a specific level below often triggers
        // another LEVEL_SWITCHING event. Avoid the trigger/event loop.


        if (switching) {
          switching = false;
          return;
        }

        const currentVideoWidth = hls.levels[hls.currentLevel].width;
        const nextVideoWidth = data.width;
        logger.debug(`Switching level from ${hls.currentLevel} to ${data.level}`, {
          from: hls.levels[hls.currentLevel].attrs,
          to: data.attrs
        });

        if (nextVideoWidth > currentVideoWidth && (currentVideoWidth <= 0 || nextVideoWidth / currentVideoWidth >= MIN_WIDTH_RATIO)) {
          logger.log('Switching level immediately!');
          switching = true;
          hls.currentLevel = data.level;
          hls.currentLevel = -1; // Restore automatic level switching.
        }
      });
    }
  }

  registerHlsJsErrorListener(hlsClass, hls) {
    hls.on(hlsClass.Events.ERROR, (event, data) => {
      if (data.details === hlsClass.ErrorDetails.BUFFER_NUDGE_ON_STALL) {
        this.incomingAutoSeek = Date.now();
        logger.log('Incoming buffer nudge.');
        return;
      }

      if (data.details === hlsClass.ErrorDetails.BUFFER_SEEK_OVER_HOLE) {
        this.incomingAutoSeek = Date.now();
        logger.log('Incoming seek over hole.');
        return;
      } // Recovery mechanism for when hls.js reduces maxMaxBufferLength to
      // practically nothing as a result of multiple
      // DOMException.QUOTA_EXCEEDED_ERROR/buffer full errors. hls.js stops
      // loading segments and playback stalls. Restore maxMaxBufferLength to a
      // low but functional value and nudge playback.


      if (data.details === hlsClass.ErrorDetails.BUFFER_STALLED_ERROR && !data.fatal) {
        var _hls$levels$hls$curre, _hls$levels$hls$curre2;

        const targetDuration = (_hls$levels$hls$curre = (_hls$levels$hls$curre2 = hls.levels[hls.currentLevel].details) == null ? void 0 : _hls$levels$hls$curre2.targetduration) != null ? _hls$levels$hls$curre : 10;
        const minMaxBufferLength = targetDuration * 2;

        if (hls.config.maxMaxBufferLength < minMaxBufferLength) {
          logger.warn('Buffer stalled with small maxMaxBufferLength. Attempting rescue.'); // If loading doesn't pick up again, kick it.

          const loadTimeout = destroyableTimeout(() => {
            logger.warn('Buffer stalled with small maxMaxBufferLength. Calling startLoad.');
            hls.startLoad();
          }, 5000);
          this.addDestroyable(loadTimeout);
          hls.once(hlsClass.Events.BUFFER_APPENDED, () => {
            loadTimeout.destroy();
            const nudgeDistance = hls.config.maxBufferHole;
            logger.log(`Buffer stalled with small maxMaxBufferLength. Nudging +${nudgeDistance}s.`);
            this.incomingAutoSeek = Date.now();
            this.seekToRelativeTime(nudgeDistance);
          }); // When forward buffer is less, then load more, effectively making
          // the forward buffer the minimum value + one target duration.

          hls.config.maxMaxBufferLength = minMaxBufferLength;
          return;
        }
      }

      const error = new PlaybackSessionError(resolveHlsJsErrorType(data, hlsClass.ErrorDetails, this.playbackStarted), new ReducedHlsJsError(data), data.details, data.fatal);
      this.handleError(error);
    });
  }

}) || _class); // Destroyable "on" method for hls.js event listeners. Don't export.

const on = (hls, event, listener, context) => {
  hls.on(event, listener, context);
  return new class extends DestroyableBase {
    constructor(off) {
      super();
      this.off = off;
    }

    destroy() {
      this.off();
      super.destroy();
    }

  }(() => hls.off(event, listener, context));
};

export { HlsJsAdapter as H };
